import React, { useState, useEffect } from 'react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { BookOpen, Microscope, GraduationCap } from 'lucide-react';

const EducationLevelsPage = () => {
  const [levels, setLevels] = useState([]);
  const [activeLevel, setActiveLevel] = useState(null);

  useEffect(() => {
    fetchLevels();
  }, []);

  useEffect(() => {
    if (levels.length > 0 && !activeLevel) {
      setActiveLevel(levels[0].level_key);
    }
  }, [levels]);

  const fetchLevels = async () => {
    try {
      const response = await fetch('http://localhost/api/education.php', {
        credentials: 'include',
      });
      
      if (!response.ok) {
        console.error('Error fetching education levels:', response.statusText);
        return;
      }
      
      const text = await response.text();
      const data = text ? JSON.parse(text) : [];
      // Sadece aktif olanları al - tüm olası formatları kontrol et
      const activeLevels = data.filter(item => {
        const isActive = item.is_active;
        // Aktif olmayanları filtrele
        if (isActive === false || isActive === 0 || isActive === '0' || isActive === null || isActive === undefined || isActive === '') {
          return false;
        }
        // Aktif olanları al
        return isActive === true || isActive === 1 || isActive === '1';
      });
      setLevels(activeLevels);
    } catch (error) {
      console.error('Error fetching education levels:', error);
    }
  };

  const getIcon = (key) => {
    const icons = {
      ilkokul: <BookOpen className="w-8 h-8" />,
      ortaokul: <Microscope className="w-8 h-8" />,
      lise: <GraduationCap className="w-8 h-8" />,
    };
    return icons[key] || <BookOpen className="w-8 h-8" />;
  };

  const currentLevel = levels.find((l) => l.level_key === activeLevel) || levels[0];

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />

      {levels.length > 0 && currentLevel ? (
        <>
          {/* Hero Section */}
          <section className="relative h-64 mt-20" data-testid="education-hero-section">
            <div className="absolute inset-0">
              <img
                src={currentLevel.image_url || 'https://images.unsplash.com/photo-1588072432836-e10032774350'}
                alt={currentLevel.title}
                className="w-full h-full object-cover"
              />
              <div className="absolute inset-0 bg-gradient-to-r from-red-900/90 to-red-800/70" />
            </div>
            <div className="relative h-full flex items-center">
              <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                <h1 className="text-4xl sm:text-5xl font-bold text-white mb-3 fade-in-up">
                  Eğitim Kademelerimiz
                </h1>
                <p className="text-lg text-gray-100 max-w-2xl fade-in-up" style={{ animationDelay: '0.2s' }}>
                  İlkokul'dan Lise'ye kadar her kademede kaliteli ve çağdaş eğitim
                </p>
              </div>
            </div>
          </section>

          {/* Level Tabs */}
          <section className="py-8 bg-white shadow-md">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              <div className="flex flex-wrap gap-4 justify-center">
                {levels.map((level) => (
                  <button
                    key={level.id}
                    data-testid={`level-tab-${level.id}`}
                    onClick={() => setActiveLevel(level.level_key)}
                    className={`flex items-center gap-3 px-6 py-3 rounded-full font-semibold transition-all ${
                      activeLevel === level.level_key
                        ? `bg-gradient-to-r ${level.color} text-white shadow-lg`
                        : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{getIcon(level.level_key)}</span>
                    <span>{level.title}</span>
                  </button>
                ))}
              </div>
            </div>
          </section>

          {/* Level Details */}
          <section className="py-20" data-testid="level-details-section">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
              {/* Header */}
              <div className="text-center mb-16">
                <div className={`inline-flex items-center justify-center w-20 h-20 rounded-full bg-gradient-to-r ${currentLevel.color} text-white mb-6`}>
                  {getIcon(currentLevel.level_key)}
                </div>
                <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
                  {currentLevel.title}
                </h2>
                <p className="text-xl text-[#C0A062] font-semibold mb-2">{currentLevel.age_range}</p>
                <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                  {currentLevel.description}
                </p>
              </div>

              {/* Philosophy */}
              {currentLevel.philosophy && (
                <div className="mb-12 p-8 rounded-2xl bg-gradient-to-br from-white to-gray-50 border border-gray-200 shadow-md">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 flex items-center">
                    <span className="w-1 h-8 bg-[#C0A062] mr-3"></span>
                    Eğitim Felsefemiz
                  </h3>
                  <p className="text-gray-700 leading-relaxed">{currentLevel.philosophy}</p>
                </div>
              )}

              {/* Curriculum & Features */}
              {(currentLevel.curriculum && currentLevel.curriculum.length > 0) || (currentLevel.features && currentLevel.features.length > 0) ? (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                  {/* Curriculum */}
                  {currentLevel.curriculum && Array.isArray(currentLevel.curriculum) && currentLevel.curriculum.length > 0 && (
                    <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="curriculum-section">
                      <div className="flex items-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                          <span className={`w-10 h-10 rounded-full bg-gradient-to-r ${currentLevel.color} flex items-center justify-center text-white mr-3`}>
                            📚
                          </span>
                          Müfredat
                        </h3>
                      </div>
                      <ul className="space-y-3">
                        {currentLevel.curriculum.map((item, index) => (
                          <li key={index} className="flex items-start" data-testid={`curriculum-item-${index}`}>
                            <span className="w-2 h-2 bg-[#C0A062] rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {/* Features */}
                  {currentLevel.features && Array.isArray(currentLevel.features) && currentLevel.features.length > 0 && (
                    <div className="bg-white rounded-2xl p-8 shadow-lg" data-testid="features-section">
                      <div className="flex items-center mb-6">
                        <h3 className="text-2xl font-bold text-gray-900 flex items-center">
                          <span className={`w-10 h-10 rounded-full bg-gradient-to-r ${currentLevel.color} flex items-center justify-center text-white mr-3`}>
                            ✨
                          </span>
                          Öne Çıkan Özellikler
                        </h3>
                      </div>
                      <ul className="space-y-3">
                        {currentLevel.features.map((item, index) => (
                          <li key={index} className="flex items-start" data-testid={`feature-item-${index}`}>
                            <span className="w-2 h-2 bg-red-900 rounded-full mt-2 mr-3 flex-shrink-0"></span>
                            <span className="text-gray-700">{item}</span>
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              ) : (
                <div className="bg-white rounded-2xl p-8 shadow-lg text-center text-gray-500">
                  <p>Müfredat ve özellikler henüz eklenmemiş.</p>
                </div>
              )}

              {/* Image Gallery */}
              {currentLevel.image_url && (
                <div className="mt-12">
                  <img
                    src={currentLevel.image_url}
                    alt={currentLevel.title}
                    className="w-full h-96 object-cover rounded-2xl shadow-2xl"
                  />
                </div>
              )}
            </div>
          </section>
        </>
      ) : (
        <section className="py-20 mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <p className="text-gray-500">Eğitim kademeleri yükleniyor...</p>
          </div>
        </section>
      )}

      {/* CTA Section */}
      {levels.length > 0 && (
        <section className="py-20 bg-white text-gray-900">
          <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900">
              Çocuğunuzun Geleceği İçin <span className="text-[#C0A062]">Doğru Karar</span>
            </h2>
            <p className="text-xl text-gray-700 mb-8">
              Daha fazla bilgi almak veya kampüsümüzü ziyaret etmek için bize ulaşın
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a
                href="/iletisim"
                data-testid="cta-admission-btn"
                className="px-8 py-4 bg-[#C0A062] hover:bg-[#9a7d4a] text-white font-semibold rounded-full shadow-xl transition-all"
              >
                Başvuru Yap
              </a>
              <a
                href="/iletisim"
                data-testid="cta-contact-btn"
                className="px-8 py-4 bg-gray-900 hover:bg-gray-800 text-white font-semibold rounded-full shadow-xl transition-all"
              >
                Bize Ulaşın
              </a>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default EducationLevelsPage;